import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import React, { useContext, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import DatePicker from 'components/CustomInputs/DatePicker';
import IntegerNumberFormat from 'components/CustomInputs/IntegerNumberFormat';
import TimePicker from 'components/CustomInputs/TimePicker';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { AppContext } from 'context';
import { useDistributionKeys, useMaterials } from 'hooks';
import { colors, fonts } from 'theme';
import { Material, ProductionForm as ProductionFormType } from 'types';
import { useLatestClosureDate } from 'utils/closureHooks';
import { isMaterialBulk } from 'utils/formValidation';
import { productionFormSchema } from './ProductionForm.yup';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    paddingBottom: '2.5em',
  },
  fieldLine: {
    alignSelf: 'flex-start',
    marginTop: 8,
    marginBottom: 8,

    '& > div': {
      minWidth: 470,
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: 214,
    },
  },
  warning: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    fontWeight: 500,
    fontSize: '16px',
    marginTop: 8,
    marginBottom: 8,
  },
  warningIcon: {
    color: colors.darkOrange,
    marginRight: 8,
    width: '56px',
  },
  contracts: {
    textDecoration: 'underline',
    color: colors.darkOrange,
    '&:hover': {
      cursor: 'pointer',
    },
  },
  materialTable: {
    paddingTop: '50px',
    borderTop: `1px solid ${colors.greyLighten20}`,
    overflow: 'hidden',
  },
  headerCell: {
    padding: '11px 22px',
    '&:not(:last-child)': {
      borderRight: `1px solid ${colors.greyLighten20}`,
    },
  },
  bodyCell: {
    padding: '2px 22px',
    border: `1px solid ${colors.greyLighten20}`,
    maxHeight: 3,
    '&:first-child': {
      borderLeft: 'none',
    },
    '&:last-child': {
      borderRight: 'none',
    },
  },
  materialNameCell: {
    width: '20%',
  },
  conditioningTypeCell: {
    width: '20%',
    textTransform: 'capitalize',
  },
  conditioningQuantityCell: {
    width: '15%',
  },
  ballAverageWeightCell: {
    width: '20%',
  },
  disabled: {
    color: colors.grey500,
  },
  totalWeightCell: {
    width: '20%',
  },
  muiTextFieldRoot: {
    '& input': {
      textAlign: 'right',
      fontFamily: fonts.secondary,
    },
  },
  alignRight: {
    textAlign: 'right',
  },
  errorAtLeastOne: {
    color: colors.red,
  },
});

const StyledTableCell = withStyles({
  root: {
    fontFamily: fonts.secondary,
    fontSize: '16px',
  },
})(TableCell);

interface WeightProps {
  control: any;
  setValue: any;
  index: number;
  material: Material;
}

const TotalMaterialWeightField = ({ control, index, material, setValue }: WeightProps) => {
  const classes = useStyles();
  const conditioningQuantity = useWatch({
    control,
    name: `productionMaterials.${index}.conditioningQuantity`,
  });

  const totalMaterialWeight =
    isMaterialBulk(material) || null === material.averageWeight
      ? null
      : '' === conditioningQuantity
      ? ''
      : (material.averageWeight * conditioningQuantity) / 1000;

  useEffect(() => {
    setValue(`productionMaterials.${index}.weight`, totalMaterialWeight);
  }, [index, setValue, totalMaterialWeight]);

  return <WeightInputField className={classes.muiTextFieldRoot} value={totalMaterialWeight} disabled />;
};

export interface ProductionFormProps {
  onSubmit: SubmitHandler<ProductionFormType>;
  productions?: ProductionFormType;
}

const ProductionForm: React.FC<ProductionFormProps> = ({ onSubmit, productions }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'productionForm' });

  const materialId = productions?.productionMaterials?.[0]?.id;
  const materials = useMaterials(materialId ? { id: materialId } : undefined);
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const [closureMonth, twoMonthAfterClosureDate] = useLatestClosureDate(sortingCenter);
  const [distributionKeys] = useDistributionKeys(
    String(sortingCenter?.['@id']),
    closureMonth ? closureMonth : undefined,
  );

  const filteredMaterials = materials?.filter(material => {
    return distributionKeys?.some(
      distributionKey => distributionKey.material['@id'] === material['@id'] && distributionKey.distributionKey > 0,
    );
  });

  const schema = useMemo(
    () => productionFormSchema(pt, materials ?? [], closureMonth, twoMonthAfterClosureDate),
    [pt, materials, closureMonth, twoMonthAfterClosureDate],
  );

  const defaultValues = productions ?? {
    productionDate: null,
    productionTime: null,
    productionMaterials: [],
  };

  const {
    control,
    handleSubmit,
    register,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<ProductionFormType>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(schema),
  });

  return (
    <form id="production_form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="productionDate"
            control={control}
            render={({ field: { ref, onBlur, ...inputProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="productionDate"
                  label={pt('productionDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  onAccept={() => {
                    trigger('productionDate');
                  }}
                  onBlur={() => {
                    trigger('productionDate');
                    onBlur();
                  }}
                  InputLabelProps={{ required: true }}
                  {...inputProps}
                />
              </FormControl>
            )}
          />
          <Controller
            name="productionTime"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState }) => (
              <FormControl variant="outlined">
                <TimePicker
                  id="productionTime"
                  label={pt('productionTime')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{ required: true }}
                  {...inputProps}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
      {materials && distributionKeys && filteredMaterials && (
        <TableContainer className={classes.materialTable}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell className={`${classes.headerCell} ${classes.materialNameCell}`}>
                  {pt('create.material')}
                </StyledTableCell>
                <StyledTableCell className={`${classes.headerCell} ${classes.conditioningTypeCell}`}>
                  {pt('create.conditioningType')}
                </StyledTableCell>
                <StyledTableCell
                  className={`${classes.headerCell} ${classes.conditioningQuantityCell} ${classes.alignRight}`}
                >
                  {pt('create.conditioningQuantity')}
                </StyledTableCell>
                <StyledTableCell
                  className={`${classes.headerCell} ${classes.ballAverageWeightCell} ${classes.alignRight}`}
                >
                  {pt('create.ballAverageWeight')}
                </StyledTableCell>
                <StyledTableCell className={`${classes.headerCell} ${classes.totalWeightCell} ${classes.alignRight}`}>
                  {pt('create.totalWeight')}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMaterials.map((material, index) => (
                <TableRow key={`productionMaterials.${material['@id']}`}>
                  <StyledTableCell className={`${classes.bodyCell} ${classes.materialNameCell}`}>
                    <input
                      hidden
                      {...register(`productionMaterials.${index}.id`, {
                        shouldUnregister: true,
                        value: material['@id'],
                      })}
                    />
                    {material.shortName}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.bodyCell} ${classes.conditioningTypeCell}`}>
                    {t(`conditioningType.${material.conditioningType}`)}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.bodyCell} ${classes.conditioningQuantityCell}`}>
                    <Controller
                      name={`productionMaterials.${index}.conditioningQuantity`}
                      control={control}
                      shouldUnregister
                      render={({ field: { ref, ...inputProps }, fieldState }) => (
                        <>
                          {isMaterialBulk(material) ? (
                            '-'
                          ) : (
                            <TextField
                              className={classes.muiTextFieldRoot}
                              inputRef={ref}
                              {...inputProps}
                              helperText={fieldState.error?.message}
                              error={fieldState.invalid}
                              placeholder="0"
                              disabled={isMaterialBulk(material)}
                              InputProps={{
                                inputComponent: IntegerNumberFormat as any,
                              }}
                            />
                          )}
                        </>
                      )}
                    />
                  </StyledTableCell>
                  <StyledTableCell
                    className={`${classes.bodyCell} ${classes.ballAverageWeightCell} ${
                      isMaterialBulk(material) ? classes.disabled : ''
                    } ${classes.alignRight}`}
                  >
                    {isMaterialBulk(material) ? (
                      '-'
                    ) : (
                      <NumberFormat
                        decimalSeparator=","
                        decimalScale={3}
                        isNumericString
                        suffix=" t"
                        fixedDecimalScale={true}
                        displayType="text"
                        value={null !== material.averageWeight ? material.averageWeight / 1000 : null}
                      />
                    )}
                  </StyledTableCell>
                  <StyledTableCell className={`${classes.bodyCell} ${classes.totalWeightCell} ${classes.alignRight}`}>
                    {isMaterialBulk(material) ? (
                      <Controller
                        name={`productionMaterials.${index}.weight`}
                        control={control}
                        shouldUnregister
                        render={({ field: { ref, ...inputProps }, fieldState }) => (
                          <WeightInputField
                            className={classes.muiTextFieldRoot}
                            inputRef={ref}
                            {...inputProps}
                            placeholder="0,000 t"
                            helperText={fieldState.error?.message}
                            error={fieldState.invalid}
                          />
                        )}
                      />
                    ) : (
                      <TotalMaterialWeightField
                        control={control}
                        index={index}
                        material={material}
                        setValue={setValue}
                      />
                    )}
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {
            // @ts-ignore errors object is wrongly typed by react-hook-form. It expects an array while it is an object
            errors?.productionMaterials?.type && errors.productionMaterials.type === 'atLeastOne' && (
              // @ts-ignore
              <p className={classes.errorAtLeastOne}>{pt(errors.productionMaterials.message)}</p>
            )
          }
        </TableContainer>
      )}
    </form>
  );
};

export default ProductionForm;
